import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, X, Loader2, Trash2, Eye, EyeOff, Pencil, RefreshCw, GripVertical, UploadCloud, Save, Images } from "lucide-react";
import api, { API, formatApiError } from "../../lib/api";
import { GALLERY_CATEGORIES } from "../../constants/content";

const catLabel = (slug) => GALLERY_CATEGORIES.find((c) => c.slug === slug)?.label || slug;
const fmtSize = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
const authSrc = (p) => `${API}/gallery-media/${p.id}?auth=${localStorage.getItem("rb_token")}&v=${encodeURIComponent(p.updated_at || "")}`;
const inputCls = "w-full border border-line bg-white px-3.5 py-2.5 text-sm focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold";
const labelCls = "block font-mono text-[10px] tracking-[0.18em] uppercase text-slate2 mb-1.5";

export default function GalleryTab() {
  const [photos, setPhotos] = useState(null);
  const [staged, setStaged] = useState([]);
  const [drag, setDrag] = useState(false);
  const [uploadCat, setUploadCat] = useState("renovations");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [filter, setFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dragId, setDragId] = useState(null);
  const [orderDirty, setOrderDirty] = useState(false);
  const browseRef = useRef(null);
  const replaceRef = useRef(null);
  const replaceTarget = useRef(null);

  const load = () => api.get("/admin/gallery").then((r) => { setPhotos(r.data); setOrderDirty(false); }).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const stageFiles = (list) => {
    const next = [...staged];
    for (const f of Array.from(list)) {
      const ext = f.name.includes(".") ? f.name.split(".").pop().toLowerCase() : "";
      if (!["jpg", "jpeg", "png", "webp"].includes(ext)) { toast.error(`${f.name}: only JPG, PNG and WEBP photos are accepted`); continue; }
      if (f.size > 30 * 1024 * 1024) { toast.error(`${f.name} is larger than 30MB`); continue; }
      if (next.length >= 20) { toast.error("Maximum 20 photos per upload"); break; }
      next.push({ file: f, id: Math.random().toString(36).slice(2), preview: URL.createObjectURL(f), title: f.name.replace(/\.[^.]+$/, "").replace(/[_-]+/g, " ") });
    }
    setStaged(next);
  };

  const upload = async () => {
    if (!staged.length) return;
    setUploading(true);
    setProgress(0);
    const fd = new FormData();
    fd.append("category", uploadCat);
    fd.append("published", "true");
    staged.forEach((s) => fd.append("files", s.file));
    try {
      const res = await api.post("/admin/gallery/photos", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (ev) => setProgress(Math.round((ev.loaded / (ev.total || 1)) * 100)),
      });
      const titles = staged.map((s) => ({ id: s.id, title: s.title }));
      staged.forEach((s) => URL.revokeObjectURL(s.preview));
      setStaged([]);
      toast.success(`${res.data.length} photo${res.data.length > 1 ? "s" : ""} uploaded`);
      await load();
      for (const t of titles) {
        const match = res.data.find((p) => p.title !== t.title && t.title.trim());
        if (match) {
          try { await api.patch(`/admin/gallery/photos/${match.id}`, { title: t.title.trim() }); } catch {}
        }
      }
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this photo permanently?")) return;
    try {
      await api.delete(`/admin/gallery/photos/${id}`);
      setPhotos(photos.filter((p) => p.id !== id));
      toast.success("Photo deleted");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const togglePublished = async (p) => {
    try {
      await api.patch(`/admin/gallery/photos/${p.id}`, { published: !p.published });
      setPhotos(photos.map((x) => (x.id === p.id ? { ...x, published: !p.published } : x)));
      toast.success(p.published ? "Hidden from website" : "Now visible on website");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const startReplace = (p) => { replaceTarget.current = p.id; replaceRef.current?.click(); };

  const doReplace = async (file) => {
    if (!file || !replaceTarget.current) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await api.post(`/admin/gallery/photos/${replaceTarget.current}/replace`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotos(photos.map((x) => (x.id === res.data.id ? res.data : x)));
      toast.success("Photo replaced");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const res = await api.patch(`/admin/gallery/photos/${editing.id}`, {
        title: editing.title, description: editing.description, category: editing.category, published: editing.published,
      });
      setPhotos(photos.map((x) => (x.id === res.data.id ? res.data : x)));
      setEditing(null);
      toast.success("Photo details saved");
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  const onDropCard = (targetId) => {
    if (!dragId || dragId === targetId) return;
    const next = [...photos];
    const from = next.findIndex((p) => p.id === dragId);
    const to = next.findIndex((p) => p.id === targetId);
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setPhotos(next);
    setOrderDirty(true);
    setDragId(null);
  };

  const saveOrder = async () => {
    try {
      await api.post("/admin/gallery/reorder", { ordered_ids: photos.map((p) => p.id) });
      setOrderDirty(false);
      toast.success("Photo order saved");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (photos === null) return <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-gold" /></div>;

  const visible = filter === "all" ? photos : photos.filter((p) => p.category === filter);

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <h2 className="font-display font-bold text-xl text-ink">Photo Gallery <span className="text-slate2 text-sm font-sans font-normal">({photos.length})</span></h2>
        {orderDirty && (
          <button onClick={saveOrder} data-testid="gallery-save-order" className="inline-flex items-center gap-2 bg-gold hover:bg-golddark text-white font-mono text-[10px] tracking-[0.15em] uppercase px-4 py-2.5 transition-colors">
            <Save className="w-4 h-4" /> Save New Order
          </button>
        )}
      </div>

      <div className="bg-white border border-line p-5 md:p-6 mb-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <div>
            <label className={labelCls}>Upload photos into category</label>
            <select value={uploadCat} onChange={(e) => setUploadCat(e.target.value)} data-testid="gallery-upload-category" className={inputCls}>
              {GALLERY_CATEGORIES.map((c) => <option key={c.slug} value={c.slug}>{c.label}</option>)}
            </select>
          </div>
        </div>
        <div
          data-testid="gallery-dropzone"
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => { e.preventDefault(); setDrag(false); stageFiles(e.dataTransfer.files); }}
          className={`border-2 border-dashed px-6 py-10 text-center transition-colors ${drag ? "border-gold bg-gold/5" : "border-line bg-paper"}`}
        >
          <Camera className={`w-10 h-10 mx-auto mb-3 ${drag ? "text-gold" : "text-slate2/50"}`} />
          <p className="font-display font-semibold text-ink">Drag &amp; drop photos here</p>
          <p className="text-xs text-slate2 mt-1.5 mb-4">JPG, JPEG, PNG or WEBP — up to 30MB each, up to 20 at a time. Photos are automatically resized and optimised.</p>
          <button onClick={() => browseRef.current?.click()} data-testid="gallery-browse-btn" className="inline-flex items-center gap-2 bg-ink text-white font-mono text-[10px] tracking-[0.15em] uppercase px-5 py-3 hover:bg-charcoal transition-colors">
            <UploadCloud className="w-4 h-4 text-gold" /> Browse Files
          </button>
          <input ref={browseRef} type="file" multiple accept=".jpg,.jpeg,.png,.webp" className="hidden" data-testid="gallery-file-input" onChange={(e) => { stageFiles(e.target.files); e.target.value = ""; }} />
        </div>

        {staged.length > 0 && (
          <div className="mt-5" data-testid="gallery-staged">
            <p className="font-mono text-[10px] tracking-[0.18em] uppercase text-slate2 mb-3">Ready to upload ({staged.length}) — check titles, then upload</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {staged.map((s) => (
                <div key={s.id} className="border border-line bg-white" data-testid={`staged-${s.id}`}>
                  <div className="relative">
                    <img src={s.preview} alt={s.title} className="w-full aspect-[4/3] object-cover" />
                    <button onClick={() => { URL.revokeObjectURL(s.preview); setStaged(staged.filter((x) => x.id !== s.id)); }} data-testid={`staged-remove-${s.id}`} className="absolute top-1.5 right-1.5 bg-ink/80 text-white p-1 hover:bg-red-600 transition-colors" aria-label={`Remove ${s.title}`}>
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div className="p-2">
                    <input value={s.title} onChange={(e) => setStaged(staged.map((x) => (x.id === s.id ? { ...x, title: e.target.value } : x)))} data-testid={`staged-title-${s.id}`} className="w-full text-xs border border-line px-2 py-1.5 focus:outline-none focus:border-gold" placeholder="Photo title" />
                    <p className="text-[10px] text-slate2/70 mt-1">{fmtSize(s.file.size)}</p>
                  </div>
                </div>
              ))}
            </div>
            <button onClick={upload} disabled={uploading} data-testid="gallery-upload-btn" className="mt-4 inline-flex items-center gap-2 bg-gold hover:bg-golddark text-white font-mono text-[11px] tracking-[0.15em] uppercase px-6 py-3 disabled:opacity-60 transition-colors">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />}
              {uploading ? `Uploading… ${progress}%` : `Upload ${staged.length} Photo${staged.length > 1 ? "s" : ""}`}
            </button>
            {uploading && (
              <div className="mt-3 h-1 bg-line overflow-hidden max-w-md"><div className="h-full bg-gold transition-[width] duration-300" style={{ width: `${progress}%` }} /></div>
            )}
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-2 mb-5" data-testid="gallery-filters">
        {[{ slug: "all", label: "All" }, ...GALLERY_CATEGORIES].map((c) => (
          <button key={c.slug} onClick={() => setFilter(c.slug)} data-testid={`gallery-filter-${c.slug}`} className={`font-mono text-[10px] tracking-[0.12em] uppercase px-3 py-2 border transition-colors ${filter === c.slug ? "bg-ink text-white border-ink" : "border-line text-slate2 hover:border-ink hover:text-ink bg-white"}`}>
            {c.label}{c.slug === "all" ? ` (${photos.length})` : ` (${photos.filter((p) => p.category === c.slug).length})`}
          </button>
        ))}
      </div>

      {visible.length === 0 ? (
        <div data-testid="gallery-empty" className="bg-white border border-line px-8 py-16 text-center">
          <Images className="w-10 h-10 text-line mx-auto mb-4" />
          <p className="font-display font-semibold text-lg text-ink">No photos here yet</p>
          <p className="text-sm text-slate2 mt-2">Drag photos into the upload area above — published photos appear in the website gallery automatically.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="gallery-grid">
          {visible.map((p) => (
            <div
              key={p.id}
              draggable
              onDragStart={() => setDragId(p.id)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => onDropCard(p.id)}
              data-testid={`gallery-photo-${p.id}`}
              className={`bg-white border cursor-grab active:cursor-grabbing ${dragId === p.id ? "border-gold opacity-60" : "border-line"}`}
            >
              <div className="relative">
                <img src={authSrc(p)} alt={p.title} className="w-full aspect-[4/3] object-cover" />
                <span className="absolute top-1.5 left-1.5 bg-ink/85 text-white font-mono text-[8px] tracking-[0.12em] uppercase px-2 py-1">{catLabel(p.category)}</span>
                {!p.published && <span className="absolute top-1.5 right-1.5 bg-orange-500 text-white font-mono text-[8px] tracking-[0.12em] uppercase px-2 py-1">Hidden</span>}
                <GripVertical className="absolute bottom-1.5 right-1.5 w-4 h-4 text-white/70" />
              </div>
              <div className="p-2.5">
                <p className="text-xs font-semibold text-ink truncate">{p.title}</p>
                <div className="flex items-center gap-1 mt-2">
                  <button onClick={() => setEditing({ ...p })} data-testid={`gallery-edit-${p.id}`} title="Edit title & details" className="p-1.5 text-slate2 hover:text-ink border border-line hover:border-ink transition-colors"><Pencil className="w-3.5 h-3.5" /></button>
                  <button onClick={() => startReplace(p)} data-testid={`gallery-replace-${p.id}`} title="Replace photo" className="p-1.5 text-slate2 hover:text-ink border border-line hover:border-ink transition-colors"><RefreshCw className="w-3.5 h-3.5" /></button>
                  <button onClick={() => togglePublished(p)} data-testid={`gallery-toggle-${p.id}`} title={p.published ? "Hide from website" : "Show on website"} className={`p-1.5 border transition-colors ${p.published ? "text-golddark border-line hover:border-gold" : "text-slate2 border-line hover:border-ink"}`}>
                    {p.published ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                  </button>
                  <button onClick={() => remove(p.id)} data-testid={`gallery-delete-${p.id}`} title="Delete photo" className="p-1.5 text-red-600 border border-line hover:border-red-300 transition-colors ml-auto"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      <input ref={replaceRef} type="file" accept=".jpg,.jpeg,.png,.webp" className="hidden" data-testid="gallery-replace-input" onChange={(e) => { doReplace(e.target.files?.[0]); e.target.value = ""; }} />

      {editing && (
        <div className="fixed inset-0 z-50 bg-ink/60 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-white w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="gallery-editor">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-semibold text-lg text-ink">Photo Details</h3>
              <button onClick={() => setEditing(null)} data-testid="gallery-editor-close" className="p-1.5 text-slate2 hover:text-ink"><X className="w-5 h-5" /></button>
            </div>
            <img src={authSrc(editing)} alt="" className="w-full aspect-video object-cover border border-line mb-4" />
            <div className="space-y-4">
              <div>
                <label className={labelCls}>Title</label>
                <input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="gallery-edit-title" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Description (optional)</label>
                <textarea rows={2} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} data-testid="gallery-edit-description" className={`${inputCls} resize-y`} placeholder="Short note shown with the photo" />
              </div>
              <div>
                <label className={labelCls}>Category</label>
                <select value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} data-testid="gallery-edit-category" className={inputCls}>
                  {GALLERY_CATEGORIES.map((c) => <option key={c.slug} value={c.slug}>{c.label}</option>)}
                </select>
              </div>
              <label className="inline-flex items-center gap-2.5 text-sm text-ink cursor-pointer">
                <input type="checkbox" checked={!!editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} data-testid="gallery-edit-published" className="w-4 h-4 accent-[#C88E38]" />
                Visible on the public website
              </label>
            </div>
            <button onClick={saveEdit} disabled={saving} data-testid="gallery-edit-save" className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-gold hover:bg-golddark text-white font-mono text-[11px] tracking-[0.15em] uppercase px-4 py-3 disabled:opacity-60 transition-colors">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Details
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
